/**
 * Unit conversion utilities for dimensions
 * All dimensions are stored internally in inches
 */

export type DimensionUnit = 'in' | 'mm' | 'cm';

export const convertToInches = (value: number, fromUnit: DimensionUnit): number => {
  if (isNaN(value) || !isFinite(value)) return 0;
  switch (fromUnit) {
    case 'mm': return value / 25.4;
    case 'cm': return value / 2.54;
    default: return value;
  }
};

export const convertFromInches = (value: number, toUnit: DimensionUnit): number => {
  if (isNaN(value) || !isFinite(value)) return 0;
  switch (toUnit) {
    case 'mm': return value * 25.4;
    case 'cm': return value * 2.54;
    default: return value;
  }
};

export const roundToDecimal = (value: number, decimals: number = 2): number => {
  if (isNaN(value) || !isFinite(value)) return 0;
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Format dimension value with unit label
 */
export const formatDimension = (valueInInches: number, unit: DimensionUnit): string => {
  const converted = convertFromInches(valueInInches, unit);
  const rounded = roundToDecimal(converted);
  return `${rounded} ${unit}`;
};

/**
 * Format dimensions string (e.g., "4 in × 4 in × 4 in")
 */
export const formatDimensions = (
  length: number,
  width: number,
  height: number,
  unit: DimensionUnit
): string => {
  return `${formatDimension(length, unit)} × ${formatDimension(width, unit)} × ${formatDimension(height, unit)}`;
};

/**
 * Format open dimensions string (e.g., "12.5 in × 16.5 in")
 */
export const formatOpenDimensions = (
  openLength: number,
  openWidth: number,
  unit: DimensionUnit
): string => {
  return `${formatDimension(openLength, unit)} × ${formatDimension(openWidth, unit)}`;
};
